import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { onSessionExpired, request, setAccessToken } from '@/api/client'
import { authApi } from '@/api/endpoints'
import type { CurrentUser } from '@/types'

interface AuthState {
  user: CurrentUser | null
  status: 'loading' | 'authenticated' | 'anonymous'
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  can: (permission: string) => boolean
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null)
  const [status, setStatus] = useState<AuthState['status']>('loading')

  useEffect(() => {
    onSessionExpired(() => { setUser(null); setStatus('anonymous'); setAccessToken(null) })
  }, [])

  // On boot the access token is gone (memory only), but the refresh cookie may still
  // be valid — so try to restore the session before showing the login screen.
  useEffect(() => {
    let cancelled = false
    const restore = async () => {
      try {
        const body = await request<{ access_token: string }>('/auth/refresh', {
          method: 'POST', skipAuthRetry: true,
        })
        setAccessToken(body.access_token)
        const me = await authApi.me()
        if (!cancelled) { setUser(me); setStatus('authenticated') }
      } catch {
        if (!cancelled) { setStatus('anonymous') }
      }
    }
    void restore()
    return () => { cancelled = true }
  }, [])

  const login = useCallback(async (email: string, password: string) => {
    const result = await authApi.login(email, password)
    setAccessToken(result.token.access_token)
    setUser(result.user)
    setStatus('authenticated')
  }, [])

  const logout = useCallback(async () => {
    try { await authApi.logout() } finally {
      setAccessToken(null); setUser(null); setStatus('anonymous')
    }
  }, [])

  const can = useCallback(
    (permission: string) => Boolean(user?.permissions.includes(permission)),
    [user],
  )

  const value = useMemo(
    () => ({ user, status, login, logout, can }),
    [user, status, login, logout, can],
  )
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used inside AuthProvider')
  return context
}

/** Hides UI the user cannot act on. Cosmetic only — the API is the authority. */
export function Can({ permission, children, fallback = null }: {
  permission: string; children: ReactNode; fallback?: ReactNode
}) {
  const { can } = useAuth()
  return <>{can(permission) ? children : fallback}</>
}
